import { Box, useTheme } from '@mui/material';
import {
  Card as CardUI,
  CardContent,
  CardHeadlineHeader,
  CardProps,
  CardTagHeader,
} from '@portals/core/src/components/Card/Card';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import type { CustomVariant as TypographyVariants } from '@portals/core/src/components/Typography/Typography.types.d';
import type { Theme } from '@portals/core/src/themes/themes';
import React from 'react';

import { ContentCard as CardModel } from '../../../models';
import { useLinkProps } from '../../hooks/useLinkProps';
import { AspectMedia, MEDIA_SIZES as ASPECT_MEDIA_SIZES } from '../AspectMedia/AspectMedia';
import { TopInfoBox } from '../TopInfoBox/TopInfoBox';

export type ContentCardVariants = Extract<
  CardProps['variant'],
  | 'content:recommendation'
  | 'content:heroThirdRight'
  | 'content:heroThirdLeft'
  | 'content:heroHalfRight'
  | 'content:heroHalfLeft'
  | 'content:oneHalf'
  | 'content:oneThird'
  | 'content:list'
  | 'content:small'
  | 'wallStart'
  | 'wall'
  | 'seminar'
>;

export interface Props {
  node: CardModel;
  variant?: ContentCardVariants;
}

// TODO: layout

const REQUIRES_16x9_MEDIA: {
  [key in ContentCardVariants]-?: boolean | { xs: string; sm: string; md: string; lg: string };
} = {
  'content:recommendation': { xs: 'none', sm: 'block', md: 'block', lg: 'block' },
  'content:heroThirdRight': true,
  'content:heroThirdLeft': true,
  'content:heroHalfRight': { xs: 'block', sm: 'none', md: 'none', lg: 'none' },
  'content:heroHalfLeft': { xs: 'block', sm: 'none', md: 'none', lg: 'none' },
  'content:oneHalf': true,
  'content:oneThird': true,
  'content:small': { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
  'content:list': false,
  wallStart: true,
  wall: true,
  seminar: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
};
const REQUIRES_4x3_MEDIA: { [key in ContentCardVariants]-?: boolean } = {
  'content:recommendation': false,
  'content:heroThirdRight': false,
  'content:heroThirdLeft': false,
  'content:heroHalfRight': false,
  'content:heroHalfLeft': false,
  'content:oneHalf': false,
  'content:oneThird': false,
  'content:small': false,
  'content:list': false,
  wallStart: false,
  wall: false,
  seminar: false,
};
const REQUIRES_1x1_MEDIA: {
  [key in ContentCardVariants]-?: boolean | { xs: string; sm: string; md: string; lg: string };
} = {
  'content:recommendation': { xs: 'block', sm: 'none', md: 'none', lg: 'none' },
  'content:heroThirdRight': false,
  'content:heroThirdLeft': false,
  'content:heroHalfRight': { xs: 'none', sm: 'block', md: 'block', lg: 'block' },
  'content:heroHalfLeft': { xs: 'none', sm: 'block', md: 'block', lg: 'block' },
  'content:oneHalf': false,
  'content:oneThird': false,
  'content:small': { xs: 'block', sm: 'block', md: 'block', lg: 'none' },
  'content:list': true,
  wallStart: false,
  wall: false,
  seminar: { xs: 'block', sm: 'block', md: 'block', lg: 'none' },
};

const HEADLINE_TYPOGRAPHY_VARIANT: { [key in ContentCardVariants]-?: TypographyVariants } = {
  'content:recommendation': 'c3',
  'content:heroThirdRight': 'c2',
  'content:heroThirdLeft': 'c2',
  'content:heroHalfRight': 'c3',
  'content:heroHalfLeft': 'c3',
  'content:oneHalf': 'c3',
  'content:oneThird': 'c3',
  'content:small': 'c3',
  'content:list': 'c3',
  wallStart: 'c3',
  wall: 'c3',
  seminar: 'c3',
};

const MEDIA_SIZES: { [key in ContentCardVariants]-?: string } = {
  'content:recommendation': ASPECT_MEDIA_SIZES.sm,
  'content:heroThirdRight': ASPECT_MEDIA_SIZES.lg,
  'content:heroThirdLeft': ASPECT_MEDIA_SIZES.lg,
  'content:heroHalfRight': ASPECT_MEDIA_SIZES.md,
  'content:heroHalfLeft': ASPECT_MEDIA_SIZES.md,
  'content:oneHalf': ASPECT_MEDIA_SIZES.md,
  'content:oneThird': ASPECT_MEDIA_SIZES.sm,
  'content:list': ASPECT_MEDIA_SIZES.sm,
  'content:small': ASPECT_MEDIA_SIZES.sm,
  wallStart: ASPECT_MEDIA_SIZES.sm,
  wall: ASPECT_MEDIA_SIZES.sm,
  seminar: ASPECT_MEDIA_SIZES.sm,
};

export const ContentCard: React.FunctionComponent<Props> = ({ node, variant = 'content:oneThird' }: Props) => {
  const { linkProps, anchorProps } = useLinkProps(node.link ? node.link[0] : null);
  const theme = useTheme<Theme>();

  const display_16x9 = REQUIRES_16x9_MEDIA[variant];
  const display_1x1 = REQUIRES_1x1_MEDIA[variant];
  const display_4x3 = REQUIRES_4x3_MEDIA[variant];

  const cardDate = new Date(node['date']).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });

  return (
    <CardUI
      id={`${node.__contentType}.${node.id}`}
      variant={variant}
      {...linkProps}
      sx={{ paddingTop: { xs: theme.spacing(5), md: theme.spacing(0), lg: theme.spacing(0) } }}
    >
      {node.image?.media_16x9 && display_16x9 ? (
        <AspectMedia
          media={node.image.media_16x9}
          aspectWidth={16}
          aspectHeight={9}
          mediaSizes={MEDIA_SIZES[variant]}
          sx={typeof display_16x9 === 'object' ? { display: display_16x9 } : {}}
        />
      ) : null}
      {node.image?.media_4x3 && display_4x3 ? (
        <AspectMedia media={node.image.media_4x3} aspectWidth={4} aspectHeight={3} mediaSizes={MEDIA_SIZES[variant]} />
      ) : null}
      {node.image?.media_1x1 && display_1x1 ? (
        <AspectMedia
          media={node.image.media_1x1}
          aspectWidth={1}
          aspectHeight={1}
          mediaSizes={MEDIA_SIZES[variant]}
          sx={typeof display_1x1 === 'object' ? { display: display_1x1 } : {}}
        />
      ) : null}
      {node.tags?.length ? (
        <CardTagHeader>
          <Typography variant="h5" component="div">
            {node.tags[0].name}
          </Typography>
        </CardTagHeader>
      ) : null}
      {/* Using empty box to implement two rowgaps for the desired spacing */}
      <Box />
      {node.overline || node.link?.length > 0 ? (
        <CardHeadlineHeader>
          {node.overline?.text ? (
            <Typography
              variant="h6"
              component={node.overline.component}
              color={theme.palette.text.overline}
              innerPadding="both"
            >
              {node.overline.text}
            </Typography>
          ) : null}
          {node.link?.length ? (
            <Typography
              color={theme.palette.text.primary}
              variant={HEADLINE_TYPOGRAPHY_VARIANT[variant]}
              {...anchorProps}
              innerPadding="both"
            >
              {node.link[0].text}
            </Typography>
          ) : null}
        </CardHeadlineHeader>
      ) : null}

      <CardContent>
        {node.text && (
          <Typography
            variant={variant === 'content:recommendation' ? 'c3' : 'body2'}
            color="text.secondary"
            innerPadding="both"
          >
            {node.text}
          </Typography>
        )}
        {node.benefits && <TopInfoBox node={{ ...node.benefits, size: 'small' }} />}
        {node.date && (
          <Typography variant="h6" component="div" color="grey.dark" innerPadding="both">
            {cardDate}
          </Typography>
        )}
      </CardContent>
    </CardUI>
  );
};
